import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import BrandLockup from "../components/brand/BrandLockup";

import { ApiError, api } from "../api/client";
import { useAuth } from "../context/AuthContext";
import type { AuthMethodResponse, LoginMode, Me } from "../types";
import {
  fetchAuthMethod,
  loginWithPasskey,
  passkeyAvailable,
} from "../utils/webauthn";
import { Delete, Fingerprint } from "lucide-react";

type Stage = "identifier" | "pin-or-passkey";
type IdentifierKind = "employee_number" | "email";

export default function LoginScreen() {
  const { t } = useTranslation();
  const { employee, isKiosk, login, onLoggedIn } = useAuth();
  const [mode, setMode] = useState<LoginMode>("personal");
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as { from?: string; email?: string };
  // Where they were heading when asked to sign in (e.g. the documents link in
  // an email). Same-site paths only.
  const from =
    state.from && state.from.startsWith("/") && !state.from.startsWith("//") && state.from !== "/"
      ? state.from
      : null;

  useEffect(() => {
    if (!employee) return;
    if (employee.must_change_pin && !isKiosk) {
      navigate(from && from.startsWith("/me") ? from : "/me", { replace: true });
    } else if (from && !isKiosk) {
      navigate(from, { replace: true });
    } else {
      // Personal devices land on the home page; shared kiosks go to the clock.
      navigate(isKiosk ? "/clock" : "/home", { replace: true });
    }
  }, [employee, isKiosk, navigate, from]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-gradient-to-b from-pme-sky via-pme-paper to-pme-paper safe-x">
      <div className="w-full max-w-sm px-6 py-8">
        <div className="mb-8 flex flex-col items-center">
          <BrandLockup stacked size="lg" />
        </div>

        <div className="mb-6 flex rounded-full border border-gray-200 bg-gray-50 p-1 text-sm">
          <ModeTab current={mode} value="personal" onClick={setMode}>
            {t("auth.personalDevice", { defaultValue: "Personal Device" })}
          </ModeTab>
          <ModeTab current={mode} value="kiosk" onClick={setMode}>
            {t("auth.kiosk", { defaultValue: "Kiosk" })}
          </ModeTab>
        </div>

        {mode === "personal" ? (
          <PersonalLogin
            onLogin={login}
            onLoggedIn={onLoggedIn}
            initialIdentifier={state.email ?? ""}
          />
        ) : (
          <KioskLogin onLogin={login} />
        )}
      </div>
    </div>
  );
}

function ModeTab({
  current,
  value,
  onClick,
  children,
}: {
  current: LoginMode;
  value: LoginMode;
  onClick: (m: LoginMode) => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={[
        "flex-1 rounded-full px-3 py-1.5 font-medium transition",
        active ? "bg-white text-pme-blue shadow" : "text-gray-500",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

// ===================== PERSONAL ====================

function PersonalLogin({
  onLogin,
  onLoggedIn,
  initialIdentifier = "",
}: {
  onLogin: (opts: { identifier: string; pin: string; mode: "personal" }) => Promise<Me>;
  onLoggedIn: () => Promise<Me>;
  initialIdentifier?: string;
}) {
  const { t } = useTranslation();
  const [stage, setStage] = useState<Stage>("identifier");
  const [showForgotPin, setShowForgotPin] = useState(false);
  const [kind, setKind] = useState<IdentifierKind>("email");
  const [identifier, setIdentifier] = useState(initialIdentifier);
  const [pin, setPin] = useState("");
  const [method, setMethod] = useState<AuthMethodResponse | null>(null);
  const [platformPasskeySupported, setPlatformPasskeySupported] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void passkeyAvailable().then(setPlatformPasskeySupported);
  }, []);

  const lookUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const m = await fetchAuthMethod(identifier.trim());
      setMethod(m);
      setStage("pin-or-passkey");
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 404
          ? t("auth.noAccount", { defaultValue: "No account matches that identifier." })
          : err instanceof ApiError
            ? err.message
            : t("common.error"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitPin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onLogin({ identifier: identifier.trim(), pin, mode: "personal" });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const runPasskey = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await loginWithPasskey(identifier.trim());
      await onLoggedIn();
    } catch (err) {
      setError((err as Error).message || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (stage === "identifier") {
    return (
      <form onSubmit={lookUp} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700" htmlFor="identifier">
            {kind === "employee_number" ? t("auth.employeeId") : t("auth.emailOrPersonal")}
          </label>
          <input
            id="identifier"
            type={kind === "email" ? "email" : "text"}
            autoComplete={kind === "email" ? "username" : "off"}
            inputMode={kind === "email" ? "email" : "text"}
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            required
            placeholder={kind === "employee_number" ? "PME-0001" : t("auth.emailPlaceholder")}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-base focus:border-pme-blue focus:outline-none focus:ring-2 focus:ring-pme-blue/30"
          />
          <button
            type="button"
            onClick={() =>
              setKind((k) => (k === "email" ? "employee_number" : "email"))
            }
            className="mt-2 text-xs text-pme-blue hover:underline"
          >
            {kind === "employee_number" ? t("auth.useEmailInstead") : t("auth.useIdInstead")}
          </button>
        </div>

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="block w-full rounded-lg bg-pme-blue py-3 text-base font-semibold text-white shadow transition active:scale-[0.99] disabled:opacity-60"
        >
          {submitting ? t("common.loading") : t("auth.continue")}
        </button>
      </form>
    );
  }

  // pin-or-passkey stage
  const passkeyAvailableForUser =
    platformPasskeySupported &&
    method &&
    method.has_passkeys &&
    (method.auth_method === "passkey" || method.auth_method === "both");

  const pinAvailable =
    method && (method.auth_method === "pin" || method.auth_method === "both");

  return (
    <div className="space-y-4">
      {method && (
        <p className="text-center text-sm text-gray-600">
          Hello, <span className="font-semibold">{method.first_name}</span>
        </p>
      )}

      {passkeyAvailableForUser && (
        <button
          type="button"
          onClick={runPasskey}
          disabled={submitting}
          className="flex w-full items-center justify-center gap-3 rounded-lg bg-pme-blue py-4 text-base font-semibold text-white disabled:opacity-60"
        >
          <Fingerprint size={20} strokeWidth={1.75} aria-hidden />
          Use Fingerprint / Face ID
        </button>
      )}

      {pinAvailable && !showForgotPin && (
        <form onSubmit={submitPin} className="space-y-3">
          {passkeyAvailableForUser && (
            <p className="text-center text-xs text-gray-400">or use your PIN</p>
          )}
          <label className="block text-sm font-medium text-gray-700" htmlFor="pin">
            PIN
          </label>
          <input
            id="pin"
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={12}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            required
            autoFocus
            className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-base tracking-widest focus:border-pme-blue focus:outline-none focus:ring-2 focus:ring-pme-blue/30"
          />
          <button
            type="submit"
            disabled={submitting}
            className="block w-full rounded-lg border border-pme-blue py-3 text-base font-semibold text-pme-blue disabled:opacity-60"
          >
            {submitting ? t("auth.signingIn") : t("auth.login")}
          </button>
          <button
            type="button"
            onClick={() => setShowForgotPin(true)}
            className="block w-full text-center text-xs text-gray-500 hover:text-pme-blue hover:underline"
          >
            {t("auth.forgotPin")}
          </button>
        </form>
      )}

      {showForgotPin && (
        <ForgotPinFlow
          defaultIdentifier={identifier}
          onDone={onLoggedIn}
          onCancel={() => setShowForgotPin(false)}
        />
      )}

      {method?.auth_method === "passkey" && !method.has_passkeys && (
        <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
          Your account is passkey-only but no passkey is registered yet. Please
          ask an administrator to switch you to PIN mode so you can set one up.
        </p>
      )}
      {platformPasskeySupported === false && method?.has_passkeys && (
        <p className="rounded-lg bg-gray-50 p-2 text-xs text-gray-500">
          {t("auth.passkey.notAvailable")}
        </p>
      )}

      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={() => {
          setStage("identifier");
          setPin("");
          setMethod(null);
          setError(null);
        }}
        className="w-full text-center text-xs text-gray-500 hover:underline"
      >
        {t("common.back")}
      </button>
    </div>
  );
}

// ===================== KIOSK =======================

// ===================== FORGOT PIN ====================

type ForgotStep = "request" | "code" | "new-pin";

function ForgotPinFlow({
  defaultIdentifier,
  onDone,
  onCancel,
}: {
  defaultIdentifier: string;
  onDone: () => Promise<Me>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState<ForgotStep>("request");
  const [kind, setKind] = useState<IdentifierKind>("email");
  const [identifier, setIdentifier] = useState(defaultIdentifier);
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const requestCode = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.post<{ message: string; email_paused?: boolean }>(
        "/api/auth/forgot-pin",
        { identifier: identifier.trim() },
      );
      if (res.email_paused) {
        // No code can arrive while email is paused: stay here and send them to HR.
        setInfo(t("auth.emailPausedAskHr"));
        return;
      }
      setInfo(t("auth.codeSent"));
      setStep("code");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post<{ valid: boolean; reset_token: string }>(
        "/api/auth/verify-reset-code",
        { identifier: identifier.trim(), code: code.trim() },
      );
      setResetToken(res.reset_token);
      setStep("new-pin");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const submitNewPin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (newPin !== confirmPin) {
      setError(t("auth.pinMismatch"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/api/auth/reset-pin", {
        reset_token: resetToken,
        new_pin: newPin,
      });
      setInfo(t("auth.pinUpdated"));
      await onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const dots = (
    <div className="mb-4 flex items-center justify-center gap-2">
      {(["request", "code", "new-pin"] as const).map((s) => (
        <span
          key={s}
          className={[
            "h-2 w-2 rounded-full",
            step === s ? "bg-pme-blue" : "bg-gray-300",
          ].join(" ")}
        />
      ))}
    </div>
  );

  if (step === "request") {
    return (
      <form onSubmit={requestCode} className="space-y-4">
        {dots}
        <h2 className="text-center text-lg font-semibold text-gray-900">
          {t("auth.resetPin")}
        </h2>
        <p className="text-center text-sm text-gray-500">
          {t("auth.resetInstructions")}
        </p>
        <div>
          <label
            className="block text-sm font-medium text-gray-700"
            htmlFor="forgot-id"
          >
            {kind === "email" ? t("auth.email") : t("auth.employeeId")}
          </label>
          <input
            id="forgot-id"
            type={kind === "email" ? "email" : "text"}
            inputMode={kind === "email" ? "email" : "text"}
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            required
            placeholder={kind === "email" ? "you@pmecake.co.uk" : "PME-0001"}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-base focus:border-pme-blue focus:outline-none focus:ring-2 focus:ring-pme-blue/30"
          />
          <button
            type="button"
            onClick={() =>
              setKind((k) => (k === "email" ? "employee_number" : "email"))
            }
            className="mt-2 text-xs text-pme-blue hover:underline"
          >
            {kind === "email"
              ? t("auth.useIdInstead")
              : t("auth.useEmailInstead")}
          </button>
        </div>
        {info && (
          <p role="status" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            {info}
          </p>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="block w-full rounded-lg bg-pme-blue py-3 text-base font-semibold text-white disabled:opacity-60"
        >
          {submitting ? t("common.loading") : t("auth.sendCode")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="block w-full text-center text-xs text-gray-500 hover:underline"
        >
          {t("auth.backToLogin")}
        </button>
      </form>
    );
  }

  if (step === "code") {
    return (
      <form onSubmit={verifyCode} className="space-y-4">
        {dots}
        <h2 className="text-center text-lg font-semibold text-gray-900">
          {t("auth.enterCode")}
        </h2>
        <p className="text-center text-sm text-gray-500">
          {t("auth.codeSentTo")}
        </p>
        {info && <p className="text-center text-xs text-green-600">{info}</p>}
        <div className="flex justify-center">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            required
            autoFocus
            placeholder="000000"
            className="w-48 rounded-lg border border-gray-300 px-4 py-4 text-center text-2xl font-bold tracking-[0.5em] focus:border-pme-blue focus:outline-none focus:ring-2 focus:ring-pme-blue/30"
          />
        </div>
        {error && <p className="text-center text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={submitting || code.length < 6}
          className="block w-full rounded-lg bg-pme-blue py-3 text-base font-semibold text-white disabled:opacity-60"
        >
          {submitting ? t("common.loading") : t("auth.verifyCode")}
        </button>
        <button
          type="button"
          onClick={() => {
            setCode("");
            setError(null);
            setInfo(null);
            setStep("request");
          }}
          className="block w-full text-center text-xs text-pme-blue hover:underline"
        >
          {t("auth.didntReceive")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="block w-full text-center text-xs text-gray-500 hover:underline"
        >
          {t("auth.backToLogin")}
        </button>
      </form>
    );
  }

  // step === "new-pin"
  return (
    <form onSubmit={submitNewPin} className="space-y-4">
      {dots}
      <h2 className="text-center text-lg font-semibold text-gray-900">
        {t("auth.setNewPin")}
      </h2>
      <div>
        <label className="block text-sm font-medium text-gray-700">
          {t("auth.newPin")}
        </label>
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={12}
          value={newPin}
          onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
          required
          autoFocus
          className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-base tracking-widest focus:border-pme-blue focus:outline-none focus:ring-2 focus:ring-pme-blue/30"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">
          {t("auth.confirmNewPin")}
        </label>
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={12}
          value={confirmPin}
          onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
          required
          className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-3 text-base tracking-widest focus:border-pme-blue focus:outline-none focus:ring-2 focus:ring-pme-blue/30"
        />
      </div>
      <p className="text-xs text-gray-400">{t("auth.pinRequirements")}</p>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {info && <p className="text-sm text-green-600">{info}</p>}
      <button
        type="submit"
        disabled={submitting || newPin.length < 6}
        className="block w-full rounded-lg bg-pme-blue py-3 text-base font-semibold text-white disabled:opacity-60"
      >
        {submitting ? t("common.loading") : t("auth.resetAndSignIn")}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="block w-full text-center text-xs text-gray-500 hover:underline"
      >
        {t("auth.backToLogin")}
      </button>
    </form>
  );
}

// ===================== KIOSK =======================

function KioskLogin({
  onLogin,
}: {
  onLogin: (opts: { pin: string; mode: "kiosk" }) => Promise<Me>;
}) {
  const { t } = useTranslation();
  const [digits, setDigits] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);

  const submit = async (pin: string) => {
    setSubmitting(true);
    setError(null);
    try {
      await onLogin({ pin, mode: "kiosk" });
    } catch (err) {
      setShake(true);
      setDigits("");
      setError(err instanceof ApiError ? err.message : t("auth.incorrectPin"));
      window.setTimeout(() => setShake(false), 400);
    } finally {
      setSubmitting(false);
    }
  };

  const press = (digit: string) => {
    if (submitting) return;
    if (digits.length >= 12) return;
    const next = digits + digit;
    setDigits(next);
    if (next.length >= 6) {
      void submit(next);
    }
  };

  const backspace = () => setDigits((d) => d.slice(0, -1));
  const clear = () => setDigits("");

  return (
    <div className="space-y-5">
      <p className="text-center text-sm text-gray-600">
        {t("auth.enterPinKiosk", { defaultValue: "Enter your PIN to clock in." })}
      </p>

      <div
        className={[
          "mx-auto flex items-center justify-center gap-3",
          shake ? "animate-[shake_0.4s]" : "",
        ].join(" ")}
        aria-live="polite"
      >
        {Array.from({ length: Math.max(6, digits.length) }).map((_, i) => {
          const filled = i < digits.length;
          return (
            <span
              key={i}
              className={[
                "h-4 w-4 rounded-full border",
                filled ? "border-pme-blue bg-pme-blue" : "border-gray-300",
              ].join(" ")}
            />
          );
        })}
      </div>

      {error && (
        <p role="alert" className="text-center text-sm text-red-600">
          {error}
        </p>
      )}

      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <KeypadButton key={n} onClick={() => press(String(n))} disabled={submitting}>
            {n}
          </KeypadButton>
        ))}
        <KeypadButton onClick={clear} disabled={submitting} variant="muted">
          Clear
        </KeypadButton>
        <KeypadButton onClick={() => press("0")} disabled={submitting}>
          0
        </KeypadButton>
        <KeypadButton onClick={backspace} disabled={submitting} variant="muted">
          <Delete size={22} strokeWidth={1.75} aria-label="Delete" />
        </KeypadButton>
      </div>
    </div>
  );
}

function KeypadButton({
  children,
  onClick,
  disabled,
  variant = "default",
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: "default" | "muted";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "flex h-16 items-center justify-center rounded-xl text-xl font-semibold shadow-sm active:scale-[0.98] disabled:opacity-50",
        variant === "default"
          ? "bg-white text-gray-900 border border-gray-200"
          : "bg-gray-100 text-gray-600",
      ].join(" ")}
    >
      {children}
    </button>
  );
}
